import React, { useState, useEffect } from 'react';
import { ShoppingCart, Globe, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import { authApi, shopSettingsApi } from '../api';

const LoginPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage, setCurrentUser } = useApp();
  const t = translations[language];

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [publicSettings, setPublicSettings] = useState({});

  const languages = [
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'km', name: 'ខ្មែរ', flag: '🇰🇭' }
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  useEffect(() => {
    const loadPublicSettings = async () => {
      try {
        const res = await shopSettingsApi.getPublicSettings();
        if (res.success && res.data) setPublicSettings(res.data);
      } catch (err) {}
    };
    loadPublicSettings();
  }, []);

  const getShopName = () => {
    if (!publicSettings.shop_name) return t.title || '收银台';
    if (language === 'en') return publicSettings.shop_name_en || publicSettings.shop_name;
    if (language === 'km') return publicSettings.shop_name_km || publicSettings.shop_name;
    return publicSettings.shop_name;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError(t.pleaseEnterCredentials || '请输入用户名和密码');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authApi.login({ username, password });
      if (res.success) {
        localStorage.setItem('token', res.data.token);
        setCurrentUser(res.data.user);
        navigate('/');
      } else {
        setError(res.message || t.loginFailed || '登录失败');
      }
    } catch (err) {
      setError(err.message || t.loginFailed || '登录失败，请检查用户名和密码');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    setShowLanguageMenu(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4 pb-32">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative -mt-12">

        {/* 语言切换 */}
        <div className="absolute top-4 right-4">
          <div className="relative">
            <button onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
              <Globe className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">
                {currentLanguage.flag} {currentLanguage.name}
              </span>
            </button>
            {showLanguageMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                {languages.map((lang) => (
                  <button key={lang.code} onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors flex items-center space-x-2 ${
                      language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                    }`}>
                    <span className="text-lg">{lang.flag}</span>
                    <span className="font-medium">{lang.name}</span>
                    {language === lang.code && <span className="ml-auto text-blue-600">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 标题 */}
        <div className="text-center mb-6 mt-2">
          {publicSettings.logo_url ? (
            <img src={publicSettings.logo_url} alt="logo"
              className="w-16 h-16 mx-auto mb-3 rounded-full object-cover border-4 border-blue-100 shadow" />
          ) : (
            <ShoppingCart className="w-14 h-14 mx-auto mb-3 text-blue-600" />
          )}
          <h1 className="text-2xl font-bold text-gray-800">{getShopName()}</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 用户名 */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            {/* 用户名 */}
<label className="px-4 py-3 bg-gray-50 border-r border-gray-300 text-sm font-medium text-gray-700 whitespace-nowrap min-w-[80px] text-center flex-shrink-0">
  {t.username || '用户名'}
</label>


            <input type="text" value={username}
              onChange={(e) => { setUsername(e.target.value); setError(''); }}
              className="flex-1 px-4 py-3 focus:outline-none text-sm"
              placeholder="admin" disabled={loading} required />
          </div>

          {/* 密码 */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
            {/* 密码 */}
<label className="px-4 py-3 bg-gray-50 border-r border-gray-300 text-sm font-medium text-gray-700 whitespace-nowrap min-w-[80px] text-center flex-shrink-0">
  {t.password || '密码'}
</label>
            <input type={showPassword ? 'text' : 'password'} value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className="flex-1 px-4 py-3 focus:outline-none text-sm"
              placeholder="123456" disabled={loading} required />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="px-3 text-gray-400 hover:text-gray-600 transition-colors">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            className={`w-full bg-blue-600 text-white py-3 rounded-lg font-semibold transition-colors ${
              loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'
            }`}>
            {loading ? (t.loggingIn || '登录中...') : (t.login || '登录')}
          </button>
        </form>
      </div>

      {showLanguageMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setShowLanguageMenu(false)} />
      )}
    </div>
  );
};

export default LoginPage;